// Visit types — FULL STATE fixtures + in-memory store.
// Visual reference: revQ_PracticeSetup_v13.html. No API yet (DQ-60); nothing here persists.
import { useSyncExternalStore } from "react";

export const clone = (o) => JSON.parse(JSON.stringify(o));

export const STRUCT = {
  soap: ["SOAP Note", "Subjective / Objective / Assessment / Plan", "med"],
  apso: ["APSO Note", "Assessment / Plan first, then Subjective / Objective", "med"],
  hp: ["History & Physical", "Full admission H&P structure", "med"],
  consult: ["Consult Report", "Referral-driven impression + recommendations", "med"],
  dap: ["DAP Note", "Data / Assessment / Plan", "bh"],
  birp: ["BIRP Note", "Behavior / Intervention / Response / Plan", "bh"],
  girp: ["GIRP Note", "Goal / Intervention / Response / Plan", "bh"],
  paip: ["PAIP Note", "Problem / Assessment / Intervention / Plan", "bh"],
  pie: ["PIE Note", "Problem / Intervention / Evaluation", "nurse"],
  sir: ["SIR Note", "Situation / Intervention / Response", "nurse"],
  narr: ["Narrative Note", "One free-text account, in order", "nurse"],
};
export const SGROUPS = [["med", "Medical"], ["bh", "Behavioral health"], ["nurse", "Nursing & care management"]];
export const SLOT = {
  reason: "Reason for visit", subjective: "Subjective", history: "History", objective: "Objective",
  exam: "Objective · exam", assessment: "Assessment", plan: "Plan", services: "Services performed",
  time: "Session time", narrative: "Narrative",
};
export const SLOTBY = {
  "Chief Complaint": "reason", "Reason for Consultation": "reason", "Referring Source": "reason",
  "History of Present Illness": "subjective", "Review of Systems": "exam", "Past Medical History": "history",
  "Past Surgical History": "history", "Medications": "history", "Allergies": "history", "Social History": "history",
  "Family History": "history", "Vitals": "objective", "Physical Examination": "exam",
  "Labs / Imaging Reviewed": "objective", "Assessment": "assessment", "Impression": "assessment",
  "Plan": "plan", "Recommendations / Plan": "plan",
};
export const CF = { never: "Never copy", same: "Only if same visit type", always: "Always copy" };

const SOAPS = [["Chief Complaint", "never"], ["History of Present Illness", "never"], ["Review of Systems", "never"],
  ["Past Medical History", "same"], ["Medications", "same"], ["Allergies", "same"], ["Vitals", "never"],
  ["Physical Examination", "never"], ["Assessment", "never"], ["Plan", "never"]];
const TIME = ["Session time (start · end · total minutes)", "never", 1, "time"];

export const SECDEF = {
  soap: SOAPS,
  apso: [["Assessment", "never"], ["Plan", "never"], ["Chief Complaint", "never"], ["History of Present Illness", "never"],
    ["Review of Systems", "never"], ["Past Medical History", "same"], ["Medications", "same"], ["Allergies", "same"],
    ["Vitals", "never"], ["Physical Examination", "never"]],
  consult: [["Reason for Consultation", "never"], ["Referring Source", "same"], ["History of Present Illness", "never"],
    ["Past Medical History", "same"], ["Medications", "same"], ["Allergies", "same"], ["Social History", "same"],
    ["Family History", "same", 0], ["Review of Systems", "never"], ["Vitals", "never"], ["Physical Examination", "never"],
    ["Labs / Imaging Reviewed", "never"], ["Impression", "never"], ["Recommendations / Plan", "never"]],
  hp: [["Chief Complaint", "never"], ["History of Present Illness", "never"], ["Past Medical History", "same"],
    ["Past Surgical History", "same"], ["Medications", "same"], ["Allergies", "same"], ["Family History", "same"],
    ["Social History", "same"], ["Review of Systems", "never"], ["Vitals", "never"], ["Physical Examination", "never"],
    ["Labs / Imaging Reviewed", "never"], ["Assessment", "never"], ["Plan", "never"]],
  dap: [TIME, ["Data", "never", 1, "subjective"], ["Assessment", "never", 1, "assessment"], ["Plan", "never", 1, "plan"]],
  birp: [TIME, ["Behavior", "never", 1, "subjective"], ["Intervention", "never", 1, "services"], ["Response", "never", 1, "objective"], ["Plan", "never", 1, "plan"]],
  girp: [TIME, ["Goal", "same", 1, "assessment"], ["Intervention", "never", 1, "services"], ["Response", "never", 1, "objective"], ["Plan", "never", 1, "plan"]],
  paip: [TIME, ["Problem", "same", 1, "reason"], ["Assessment", "never", 1, "assessment"], ["Intervention", "never", 1, "services"], ["Plan", "never", 1, "plan"]],
  pie: [["Problem", "never", 1, "assessment"], ["Intervention", "never", 1, "services"], ["Evaluation", "never", 1, "objective"]],
  sir: [["Situation", "never", 1, "subjective"], ["Intervention", "never", 1, "services"], ["Response", "never", 1, "objective"]],
  narr: [["Narrative", "never", 1, "narrative"]],
};
export const mkSecs = (st) => SECDEF[st].map(([l, cf, on, slot]) => ({ label: l, orig: l, cf, on: on !== 0, slot: slot || SLOTBY[l] || "subjective" }));

export const PANELS = ["Vitals", "Medications / Rx", "Orders / Requisitions", "Procedures", "Referral / Follow-up",
  "Immunization", "Quick notes", "Diet", "Exercise", "Anesthesia", "E/M code", "Payment"];
export const COLORS = ["#3B82F6", "#22C55E", "#EF4444", "#F59E0B", "#8B5CF6", "#14B8A6", "#94A3B8", "#EC4899", "#F97316"];
export const EMF = ["Psychotherapy (90832 / 90834 / 90837)", "E/M new (99202–99205)", "E/M established (99212–99215)",
  "E/M new or established", "Preventive (99381–99397)", "Medicare AWV (G0438 / G0439)", "TCM (99495 / 99496)",
  "Nurse visit (99211)", "Post-op global (99024)", "Consult (99242–99245)", "Procedure CPT", "None"];
export const CATEGORIES = ["Office visit", "Preventive", "Procedure", "Behavioral health", "Telehealth", "Phone", "Administrative"];
export const POS_OPTS = ["11 — Office", "02 — Telehealth, not in home", "10 — Telehealth, in home", "22 — Outpatient hospital", "21 — Inpatient hospital"];
export const SUGG = [
  ["Therapy session", "103T00000X · Psychologist"],
  ["Diabetes follow-up", "207RE0101X · Endocrinology"],
  ["Insulin start / education", "207RE0101X · Endocrinology"],
  ["Thyroid biopsy", "207RE0101X · Endocrinology"],
  ["Chronic care review", "207R00000X · Internal Medicine"],
  ["IPPE (Welcome to Medicare)", "207R00000X · Internal Medicine"],
];

export function vtMake(o) {
  const st = o.struct || "soap";
  return Object.assign({
    status: "Enabled", note: true, newEnc: true, prep: true, tile: false, color: COLORS[0], cat: "Office visit",
    mode: "Either", pt: "Either", dur: 30, struct: st, secs: mkSecs(st),
    panels: Object.fromEntries(PANELS.map((pn) => [pn, ["Vitals", "Medications / Rx", "Orders / Requisitions", "Referral / Follow-up", "E/M code"].includes(pn)])),
    billable: true, emReq: false, ccd: false, accum: false, emf: "E/M established (99212–99215)", pos: "11 — Office",
    telemod: "95", care: "Standard", recur: 0, obFirst: false, programs: [],
    lab: false, tocIn: false, tocAmb: false, fpe: false, refReq: false, group: false, online: false,
    labels: ["Physician", "Medical Asst"], imports: [], providers: "All providers",
    reg: { DOH: false, "NYC M-11Q": false, NYIA: false, Atteindre: false }, img: [2, 200, 250], profile: "Normal",
    overrides: {}, desc: "",
  }, o);
}

function seedVTS() {
  const vts = [
    vtMake({ id: "np", name: "New patient", abbr: "NP", dur: 60, pt: "New", struct: "hp", emf: "E/M new (99202–99205)", tile: true, color: COLORS[0], imports: ["NP", "New", "New pt"] }),
    vtMake({ id: "fu", name: "Follow-up", abbr: "F/U", dur: 30, pt: "Established", tile: true, color: COLORS[1], imports: ["FU", "F/U", "Est pt", "Recheck", "Med check", "Telehealth follow-up"] }),
    vtMake({ id: "sick", name: "Acute / sick visit", abbr: "Sick", dur: 20, tile: true, color: COLORS[3], emf: "E/M new or established", imports: ["Sick", "Acute", "Walk-in", "Urgent", "Same day"] }),
    vtMake({ id: "prev", name: "Preventive exam", abbr: "Physical", cat: "Preventive", dur: 45, mode: "In person", emf: "Preventive (99381–99397)", care: "Wellness", recur: 12, color: COLORS[4], imports: ["Annual", "Annual physical", "CPE", "Wellness", "Physical"] }),
    vtMake({ id: "awv", name: "Medicare Annual Wellness Visit", abbr: "AWV", cat: "Preventive", dur: 45, pt: "Established", emf: "Medicare AWV (G0438 / G0439)", care: "Wellness", recur: 12, color: COLORS[4], imports: ["AWV", "Medicare wellness"] }),
    vtMake({ id: "proc", name: "Procedure", abbr: "Proc", cat: "Procedure", dur: 30, mode: "In person", emf: "Procedure CPT", color: COLORS[5] }),
    vtMake({ id: "rn", name: "Nurse visit", abbr: "RN", dur: 15, pt: "Established", mode: "In person", emf: "Nurse visit (99211)", color: COLORS[6], imports: ["Nurse", "RN", "BP check", "Injection", "Vaccine"] }),
    vtMake({ id: "res", name: "Results review", abbr: "Results", dur: 15, pt: "Established", lab: true, color: COLORS[6] }),
    vtMake({ id: "preop", name: "Pre-operative evaluation", abbr: "Pre-op", dur: 30, mode: "In person", struct: "hp", emf: "E/M new or established", color: COLORS[0] }),
    vtMake({ id: "postop", name: "Post-op follow-up", abbr: "Post-op", dur: 15, pt: "Established", emf: "Post-op global (99024)", color: COLORS[5], imports: ["Post-op", "PO", "Suture removal"] }),
    vtMake({ id: "tcm", name: "Transitional care follow-up", abbr: "TCM", dur: 40, pt: "Established", mode: "In person", emf: "TCM (99495 / 99496)", programs: ["TCM"], tocAmb: true, color: COLORS[8] }),
    vtMake({ id: "consult", name: "Consultation", abbr: "Consult", dur: 60, pt: "New", struct: "consult", refReq: true, emf: "Consult (99242–99245)", color: COLORS[7] }),
    vtMake({ id: "form", name: "School / sports / work physical", abbr: "Form PE", cat: "Preventive", dur: 30, mode: "In person", billable: false, emf: "None", recur: 12, color: COLORS[2] }),
    vtMake({ id: "chart", name: "Chart review", abbr: "Review", cat: "Administrative", dur: 0, note: false, newEnc: false, prep: false, emf: "None", billable: false, color: COLORS[6] }),
  ];
  return vts;
}

export function structGroupFor(d) {
  return d.cat === "Behavioral health" ? "bh" : (d.id === "rn" || d.cat === "Administrative") ? "nurse" : "med";
}
export function secDiff(a, b) {
  let n = 0; const L = Math.max(a.length, b.length);
  for (let i = 0; i < L; i++) { const x = a[i], y = b[i]; if (!x || !y || x.orig !== y.orig || x.label !== y.label || x.on !== y.on || x.cf !== y.cf) n++; }
  return n;
}

/* ---------------- in-memory store (Full-state only) ---------------- */
let _state = { vts: seedVTS(), listdef: { imports: "fu", phone: "" } };
let _seeded = false;
const subs = new Set();
const notify = () => subs.forEach((f) => f());
function commit(vts, listdef) { _state = { vts: vts || _state.vts, listdef: listdef || _state.listdef }; notify(); }

export function subscribe(f) { subs.add(f); return () => subs.delete(f); }
export function getSnapshot() { return _state; }
export function useVisitTypes() { return useSyncExternalStore(subscribe, getSnapshot, getSnapshot); }

export function saveVisitType(vt) { commit(_state.vts.map((v) => (v.id === vt.id ? clone(vt) : v))); }
export function addVisitType(vt) { commit([..._state.vts, vt]); }
export function removeVisitType(id) { commit(_state.vts.filter((v) => v.id !== id)); }
export function setListDef(patch) { commit(null, { ..._state.listdef, ...patch }); }
export function addSuggested(name) {
  const bh = name === "Therapy session";
  const base = bh ? { abbr: "Therapy", cat: "Behavioral health", dur: 53, pt: "Established", mode: "Either", struct: "birp", emf: "Psychotherapy (90832 / 90834 / 90837)" } : {};
  addVisitType(vtMake(Object.assign({ id: "vt" + Date.now(), name, abbr: "" }, base)));
}
function updateVT(id, fn) {
  commit(_state.vts.map((v) => { if (v.id !== id) return v; const c = clone(v); fn(c); return c; }));
}
export function createOverride(vtId, npi) { updateVT(vtId, (v) => { v.overrides[npi] = clone(v.secs); }); }
export function revertOverride(vtId, npi) { updateVT(vtId, (v) => { delete v.overrides[npi]; }); }
export function saveOverride(vtId, npi, secs) { updateVT(vtId, (v) => { v.overrides[npi] = clone(secs); }); }

// One-time demo: give the first provider an override on Follow-up (PMH moved up, Assessment renamed).
export function seedDemoOverride(npi) {
  if (_seeded || !npi) return; _seeded = true;
  updateVT("fu", (v) => {
    const o = clone(v.secs);
    const i = o.findIndex((x) => x.label === "Past Medical History");
    if (i > -1) { const [pm] = o.splice(i, 1); o.splice(2, 0, pm); }
    const a = o.find((x) => x.label === "Assessment"); if (a) a.label = "Assessment (numbered problem list)";
    v.overrides[npi] = o;
  });
}
